/**
 * Content schemas — the enforcement layer for docs/04 §3 (ID rules) and the
 * GOD-4 gate ("new content must be addable as JSON with zero TypeScript changes").
 *
 * Every content file is validated at load time and by `pnpm validate:content` in
 * CI. A schema failure is a build failure, not a runtime surprise in a player's
 * save.
 */

import { z } from 'zod';

/** docs/04 §3. `+overlay` is only legal on card ids, enforced separately below. */
export const ID_PATTERN = /^[a-z]+(\.[a-zA-Z0-9]+)+(\+[a-zA-Z0-9]+)?$/;

const id = (prefix?: string) =>
  z
    .string()
    .regex(ID_PATTERN, 'must match <domain>.<segment>[.<segment>][+overlay] in lowerCamel segments')
    .refine((v) => !prefix || v.startsWith(`${prefix}.`), {
      message: `must start with '${prefix}.'`,
    });

const rarity = z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic']);
const stat = z.enum(['might', 'agility', 'wits', 'spirit']);
const trait = z.enum([
  'brave',
  'coward',
  'kind',
  'cruel',
  'greedy',
  'curious',
  'lucky',
  'honest',
  'deceptive',
]);
const significance = z.enum(['trivial', 'notable', 'pivotal', 'legendary']);
const band = z.enum(['failure', 'ordinary', 'strong', 'empowered', 'critical']);

/** Condition expressions use the npc.ts grammar. Validated for shape, not semantics. */
const condition = z
  .string()
  .max(300)
  .refine((v) => !/[;{}`$]/.test(v), {
    message: 'conditions may not contain ; { } ` or $ — they are expressions, not code',
  });

export const effectSchema = z.object({
  type: z.string().regex(/^[A-Z][A-Z_]*$/, 'event type must be SCREAMING_SNAKE_CASE'),
  target: z.string().optional(),
  significance: significance.optional(),
  data: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
  hp: z.number().int().optional(),
  gold: z.number().int().optional(),
  xp: z.number().int().nonnegative().optional(),
  fateDebt: z.number().int().optional(),
  setFlag: z.string().optional(),
  trait: trait.optional(),
});

export const choiceOutcomeSchema = z.object({
  text: z.string().min(1),
  effects: z.array(effectSchema).default([]),
  goto: id('room').optional(),
});

export const choiceSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    when: condition.optional(),
    roll: z
      .object({
        context: z.string().min(1),
        /**
         * All five bands are required. This is the schema-level enforcement of
         * "실패도 콘텐츠다" (GDD §31): an author cannot ship a roll-gated choice
         * that has nothing authored for a 1.
         */
        outcomes: z.object({
          failure: choiceOutcomeSchema,
          ordinary: choiceOutcomeSchema,
          strong: choiceOutcomeSchema,
          empowered: choiceOutcomeSchema,
          critical: choiceOutcomeSchema,
        }),
      })
      .optional(),
    outcome: choiceOutcomeSchema.optional(),
    requiresItem: id('item').optional(),
    keepItem: z.boolean().optional(),
    requiresFact: id('fact').optional(),
    revealAfterWaits: z.number().int().positive().optional(),
    sfxId: z.string().optional(),
  })
  .refine((c) => (c.roll ? !c.outcome : !!c.outcome), {
    message: 'a choice needs exactly one of `roll` or `outcome`',
  });

export const encounterSchema = z.object({
  id: id(),
  kind: z.enum(['event', 'dialogue', 'puzzle', 'trap', 'treasure', 'shop']),
  title: z.string().min(1),
  body: z.string().min(1),
  npcId: id('npc').optional(),
  monsterId: z.string().optional(),
  choices: z.array(choiceSchema).min(1),
  assetId: z.string().optional(),
  musicId: z.string().optional(),
  onEnter: z.array(effectSchema).optional(),
  once: z.boolean().optional(),
});

export const roomEdgeSchema = z.object({
  to: id('room'),
  cost: z.number().int().min(1).max(6),
  hidden: z.boolean().optional(),
  requires: z.string().optional(),
  label: z.string().optional(),
});

/**
 * Board position in grid units. A digital board game's map is *laid out*, not
 * auto-arranged: where a room sits relative to its neighbours is authored
 * information, the same way a physical board is printed.
 */
export const boardPosSchema = z.object({
  x: z.number().int().min(0).max(40),
  y: z.number().int().min(0).max(40),
});

export const roomSchema = z.object({
  id: id('room'),
  pos: boardPosSchema,
  kind: z.enum([
    'combat',
    'npc',
    'treasure',
    'trap',
    'shop',
    'puzzle',
    'event',
    'secret',
    'boss',
    'entrance',
  ]),
  name: z.string().min(1),
  description: z.string().min(1),
  edges: z.array(roomEdgeSchema),
  encounterId: id().optional(),
  monsterIds: z.array(z.string()).optional(),
  npcIds: z.array(id('npc')).optional(),
  secretId: id('secret').optional(),
  assetId: z.string().optional(),
  musicId: z.string().optional(),
  /** 쉴 수 있는 방 — 글만 여기, 회복 수치는 엔진(GDD §12-1). */
  rest: z
    .object({
      text: z.string().min(1),
      nightmare: z.string().min(1).optional(),
      deep: z.string().min(1).optional(),
    })
    .strict()
    .optional(),
  adaptiveRules: z
    .array(
      z.object({
        whenCounterAtLeast: z.tuple([z.string(), z.number().int().positive()]),
        effect: z.enum(['addTrap', 'sealDoor', 'addGuard', 'revealSecret']),
      }),
    )
    .optional(),
});

export const dungeonSchema = z.object({
  id: id('dungeon'),
  name: z.string().min(1),
  regionId: z.string().min(1),
  entrance: id('room'),
  bossRoom: id('room'),
  rooms: z.array(roomSchema).min(1),
});

export const monsterSchema = z.object({
  id: z.string().regex(ID_PATTERN),
  name: z.string().min(1),
  tier: z.enum(['common', 'elite', 'boss']),
  hp: z.number().int().positive(),
  attack: z.number().int().nonnegative(),
  defense: z.number().int().nonnegative(),
  disposition: z.enum(['feral', 'territorial', 'cunning', 'proud', 'desperate']),
  resolutions: z.array(
    z.enum([
      'strike',
      'guard',
      'feint',
      'focus',
      'invoke',
      'flee',
      'spare',
      'bargain',
      'intimidate',
      'recruit',
    ]),
  ),
  xp: z.number().int().nonnegative(),
  lootTable: z
    .array(z.object({ itemId: id('item'), weight: z.number().positive() }))
    .optional(),
  assetId: z.string().optional(),
  sfxId: z.string().optional(),
  description: z.string().optional(),
  remembersPlayer: z.boolean().optional(),
  phases: z.number().int().min(1).max(4).optional(),
});

export const npcSchema = z.object({
  id: id('npc'),
  name: z.string().min(1),
  role: z.string().min(1),
  /** GDD §8 data shape. Authored as prose so writers can work without a tool. */
  personality: z.string().min(1),
  goals: z.array(z.string()).min(1),
  fears: z.array(z.string()).default([]),
  secrets: z.array(z.string()).default([]),
  family: z.array(z.string()).default([]),
  knownFacts: z.array(id('fact')).default([]),
  /** Facts the NPC knows but will lie about at low trust (GDD §8). */
  concealedFacts: z.array(id('fact')).default([]),
  portraitAssetId: z.string().optional(),
  voiceId: z.string().optional(),
  regionId: z.string().min(1),
});

export const dialogueFragmentSchema = z.object({
  when: condition.optional(),
  text: z.string().min(1),
  priority: z.number().int().optional(),
  slot: z.string().optional(),
  once: z.boolean().optional(),
});

export const dialogueSetSchema = z.object({
  npcId: id('npc'),
  fragments: z.array(dialogueFragmentSchema).min(1),
});

/**
 * What a card DOES. Declarative so cards stay JSON — required by the GOD-4 gate
 * (content addable without TypeScript) and by GOD-8 (user cards must not run code).
 * A card with no `play` block is a record of something, not an action.
 */
export const cardPlaySchema = z.object({
  in: z.array(z.enum(['roll', 'move', 'encounter', 'combat', 'anytime'])).min(1),
  setFace: z.number().int().min(1).max(6).optional(),
  faceDelta: z.number().int().min(-5).max(5).optional(),
  bankFace: z.boolean().optional(),
  movementBonus: z.number().int().min(1).max(3).optional(),
  fateDebt: z.number().int().min(-40).max(40).optional(),
  hp: z.number().int().min(-20).max(20).optional(),
  revealSecret: id('secret').optional(),
  effects: z.array(effectSchema).optional(),
  remove: z.boolean().optional(),
});

export const cardSchema = z.object({
  id: id('card'),
  type: z.enum([
    'EVENT',
    'ACTION',
    'FATE',
    'KNOWLEDGE',
    'MEMORY',
    'RELATIONSHIP',
    'CURSE',
    'LEGEND',
  ]),
  name: z.string().min(1),
  text: z.string().min(1),
  rarity,
  /** Variants are `+overlay` ids rendered from the base art (Style Bible §4). */
  variants: z
    .array(z.object({ overlay: z.string().min(1), name: z.string().min(1), text: z.string().min(1) }))
    .optional(),
  effects: z.array(effectSchema).default([]),
  play: cardPlaySchema.optional(),
  artAssetId: z.string().optional(),
  sfxId: z.string().optional(),
  deprecated: z.boolean().optional(),
});

export const itemSchema = z.object({
  id: id('item'),
  name: z.string().min(1),
  slot: z.enum(['weapon', 'armor', 'trinket', 'consumable', 'quest', 'material']),
  rarity,
  text: z.string().min(1),
  statBonus: z.record(stat, z.number().int()).optional(),
  value: z.number().int().nonnegative().default(0),
  assetId: z.string().optional(),
  deprecated: z.boolean().optional(),
});

export const diceSchema = z.object({
  id: id('die'),
  skin: z.enum(['wooden', 'iron', 'ancient', 'fate', 'cursed', 'golden', 'ghost', 'dragon']),
  material: z.enum(['wood', 'stone', 'metal', 'magic']),
  label: z.string().min(1),
  glow: z.enum(['none', 'faint', 'medium', 'strong']),
  /**
   * Deliberately absent: any modifier, weight, luck or bonus field.
   * CLAUDE.md §4.6 — P2W is prevented by the data model, not by policy.
   */
});

export const easterEggSchema = z.object({
  id: id('egg'),
  name: z.string().min(1),
  hint: z.string().min(1),
  when: condition,
  reveal: z.string().min(1),
});

export const classSchema = z.object({
  id: z.enum(['warrior', 'rogue', 'mage', 'priest']),
  name: z.string().min(1),
  tagline: z.string().min(1),
  description: z.string().min(1),
  /** GDD §4: a 6 means something class-specific. */
  criticalAbility: z.string().min(1),
  startingItems: z.array(id('item')).default([]),
  portraitAssetId: z.string().optional(),
});

export const factSchema = z.object({
  id: id('fact'),
  label: z.string().min(1),
  /** Knowledge opens options (GDD §9); this documents which ones. */
  unlocks: z.string().optional(),
});

/**
 * `.strict()` 인 이유: 최상위 키를 하나 잘못 적으면 (`monster:` 같은) zod 가
 * 그 키를 조용히 무시하고, 해당 목록이 **빈 배열**이 된다. 검증은 오류 0건으로
 * 통과하는데 콘텐츠만 사라진다 — 가장 찾기 어려운 종류의 실패다.
 */
export const contentFileSchema = z.object({
  classes: z.array(classSchema).optional(),
  npcs: z.array(npcSchema).optional(),
  dialogue: z.array(dialogueSetSchema).optional(),
  monsters: z.array(monsterSchema).optional(),
  dungeons: z.array(dungeonSchema).optional(),
  encounters: z.array(encounterSchema).optional(),
  cards: z.array(cardSchema).optional(),
  items: z.array(itemSchema).optional(),
  dice: z.array(diceSchema).optional(),
  easterEggs: z.array(easterEggSchema).optional(),
  facts: z.array(factSchema).optional(),
}).strict();

export type ContentFile = z.infer<typeof contentFileSchema>;
export type ClassDef = z.infer<typeof classSchema>;
export type NpcDef = z.infer<typeof npcSchema>;
export type DialogueSet = z.infer<typeof dialogueSetSchema>;
export type MonsterData = z.infer<typeof monsterSchema>;
export type DungeonData = z.infer<typeof dungeonSchema>;
export type EncounterData = z.infer<typeof encounterSchema>;
export type CardDef = z.infer<typeof cardSchema>;
export type ItemDef = z.infer<typeof itemSchema>;
export type DiceData = z.infer<typeof diceSchema>;
export type EasterEggData = z.infer<typeof easterEggSchema>;
export type FactDef = z.infer<typeof factSchema>;
